import { Loader2, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/v2/accordion";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/v2/drawer";
import { Input } from "@/components/ui/v2/input";
import { Label } from "@/components/ui/v2/label";
import SearchInput from "@/components/ui/v2/search-input";
import { Separator } from "@/components/ui/v2/separator";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { URL_PARAMS } from "@/constants/urlParams";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { cn } from "@/lib/utils/cn";
import { FilterOption } from "@/types/FilterOption";

export type FilterSectionConfig =
  | {
      key: string;
      title: string;
      options: FilterOption<string>[];
    }
  | {
      key: string;
      title: string;
      type: "text";
      placeholder?: string;
    }
  | {
      key: string;
      title: string;
      type: "sources";
      sourceOptions: string[];
    }
  | {
      key: string;
      title: string;
      /**
       * Options come from the server a page at a time. For dimensions whose option count grows with the
       * data rather than with a catalogue — rendering those as one list locks up the browser.
       */
      type: "async";
    };

/** What {@link FiltersDrawerProps.useAsyncFilterOptions} must return for one `async` section. */
export type AsyncFilterOptions = {
  /** Every option loaded so far, oldest page first. */
  options: FilterOption<string>[];
  /** First page in flight. */
  isLoading: boolean;
  /** A further page in flight. */
  isFetchingMore: boolean;
  /**
   * The request failed. Distinct from an empty result: without it a failed load reads as "no options
   * match", which the user cannot tell from a genuine empty list and cannot recover from.
   */
  isError: boolean;
  /** Re-runs the failed request. */
  retry: () => void;
  hasMore: boolean;
  loadMore: () => void;
};

export type FiltersDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchParams: URLSearchParams;
  /** URL param keys this drawer reads and writes (e.g. ["department", "city", "field"]). */
  filterKeys: string[];
  /** Sections to show. Each section's key must be in filterKeys. Options can be empty while loading. */
  filterSections: FilterSectionConfig[];
  /** Entity name for drawer title and button labels (e.g. "Vacancies", "Reporting"). */
  entityName: string;
  /** Whether filter options are still loading (shows skeleton). */
  filtersLoading?: boolean;
  /** Optional total count to show above the filters. */
  totalCount?: number;
  /** Optional search term to show as a tag. */
  searchTag?: string;
  /** URL param keys to preserve when applying filters (e.g. ["search", "orderBy", "descending"]). */
  preserveParamKeys?: string[];
  /** Whether Apply and Reset All Filters set the page back to 1. Turn off for lists without pagination. */
  resetsPage?: boolean;
  /**
   * Optional hook that produces a live total count for the header from the pending (not-yet-applied)
   * filter selection. When provided, its `count` is shown in the header (with a skeleton while
   * `isLoading` is true) and supersedes `totalCount`. When omitted, the header falls back to the
   * static `totalCount` prop; if neither is provided the count panel is not rendered at all.
   *
   * Must be passed consistently (always provided or always absent) for the lifetime of this drawer
   * — toggling between defined/undefined between renders violates React's rules of hooks.
   */
  useFilteredCount?: (filters: Record<string, string[]>) => {
    count: number;
    isLoading: boolean;
  };
  /**
   * Supplies the options for every `async` section, keyed by the section's own key. Called once per
   * async section with that section's debounced search box and whether the section is expanded —
   * a collapsed section fetches nothing, so opening the drawer costs no requests.
   *
   * Required if any section has `type: "async"`. Must be passed consistently for the lifetime of this
   * drawer, for the same reason as `useFilteredCount`.
   */
  useAsyncFilterOptions?: (
    dimension: string,
    search: string,
    enabled: boolean,
  ) => AsyncFilterOptions;
  /** When true, all filter sections start expanded each time the drawer opens. */
  defaultExpanded?: boolean;
  /** Pre-selected filter values to merge into the drawer's state when it opens. Not applied to the URL until the user clicks "Show". */
  initialOverrides?: Record<string, string[]>;
};

/** Distance from the bottom of an async section's list at which the next page is requested. */
const LOAD_MORE_SCROLL_THRESHOLD = 60;

type FilterOptionRowProps = {
  option: FilterOption<string>;
  checked: boolean;
  onToggle: () => void;
  /**
   * Wrap the label mid-token rather than between words. For the identifier dimensions, whose values are
   * one long unbroken string; names read better broken at their spaces.
   */
  breakAnywhere?: boolean;
};

function FilterOptionRow({
  option,
  checked,
  onToggle,
  breakAnywhere = false,
}: FilterOptionRowProps) {
  return (
    <Label className="flex items-center gap-2 cursor-pointer hover-tint-primary transition-colors rounded-md p-2 -mx-2">
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <span
        className={cn(
          "text-sm flex-1 flex items-center gap-1.5",
          breakAnywhere ? "break-all" : "break-words",
        )}
      >
        {option.label}
        {option.badge && (
          <Badge variant="success" size="small">{option.badge}</Badge>
        )}
      </span>
      {option.count !== undefined && (
        <span className="text-xs text-muted-foreground shrink-0">
          ({option.count})
        </span>
      )}
    </Label>
  );
}

/** Toggle one value in a multi-select filter selection. */
function toggleValue(selectedValues: string[], optionValue: string): string[] {
  return selectedValues.includes(optionValue)
    ? selectedValues.filter((v) => v !== optionValue)
    : [...selectedValues, optionValue];
}

type FilterSectionProps = {
  value: string;
  title: string;
  options: FilterOption<string>[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
};

function FilterSection({
  value,
  title,
  options,
  selectedValues,
  onSelectionChange,
}: FilterSectionProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const showSearch = options.length > 5;
  const filteredOptions = showSearch
    ? options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    : options;

  return (
    <AccordionItem value={value} className="border rounded-lg last:border-b">
      <AccordionTrigger className="px-4">{t(title)}</AccordionTrigger>
      <AccordionContent className="px-4">
        <div className="space-y-2">
          {showSearch && (
            <div className="pb-2">
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("common.search")}
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto overflow-x-hidden -mx-2 px-2">
            {filteredOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                {t("commonSentences.noOptionsFound")}
              </p>
            ) : (
              filteredOptions.map((option) => (
                <FilterOptionRow
                  key={option.value}
                  option={option}
                  checked={selectedValues.includes(option.value)}
                  onToggle={() =>
                    onSelectionChange(toggleValue(selectedValues, option.value))
                  }
                />
              ))
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

type AsyncFilterSectionProps = {
  value: string;
  title: string;
  /** Whether this accordion section is open — a closed section fetches nothing. */
  expanded: boolean;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  useAsyncFilterOptions: NonNullable<
    FiltersDrawerProps["useAsyncFilterOptions"]
  >;
};

/**
 * The selections that need a row of their own above the results.
 *
 * A chosen option is usually not on the loaded pages — and a search hides it outright — so it is pinned
 * to the top; without this the user can neither see nor clear what they picked. A row stays pinned once
 * shown, so unticking one leaves it in place to be re-ticked rather than vanishing under the cursor. The
 * search term resets the set, since new results re-derive which selections are off-list.
 */
function usePinnedSelections(
  selectedValues: string[],
  loadedValues: Set<string>,
  search: string,
): string[] {
  const [pinned, setPinned] = useState({ search, values: selectedValues });
  const forSearch = pinned.search === search ? pinned.values : selectedValues;
  const missing = selectedValues.filter((v) => !forSearch.includes(v));

  // Adjusting state during render rather than in an effect: an effect would render one frame with the
  // row already gone, which is the flicker this exists to avoid. Converges — after this, the search
  // matches and nothing is missing.
  if (pinned.search !== search || missing.length > 0)
    setPinned({ search, values: [...forSearch, ...missing] });

  return forSearch.filter((v) => !loadedValues.has(v));
}

/**
 * A filter section whose options are too numerous to send at once: the search box queries the server
 * and the list loads the next page as it is scrolled. Looks and behaves like {@link FilterSection}.
 */
function AsyncFilterSection({
  value,
  title,
  expanded,
  selectedValues,
  onSelectionChange,
  useAsyncFilterOptions,
}: AsyncFilterSectionProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { options, isLoading, isFetchingMore, isError, retry, hasMore, loadMore } =
    useAsyncFilterOptions(value, debouncedSearchTerm, expanded);

  const loadedValues = useMemo(
    () => new Set(options.map((o) => o.value)),
    [options],
  );
  const pinnedValues = usePinnedSelections(
    selectedValues,
    loadedValues,
    debouncedSearchTerm,
  );

  // A failed page leaves `hasMore` true, so without the error guard scrolling would keep re-firing the
  // request that just failed. Recovery goes through the retry button instead.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isFetchingMore || isError) return;
    const el = e.currentTarget;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <
      LOAD_MORE_SCROLL_THRESHOLD;
    if (nearBottom) loadMore();
  };

  return (
    <AccordionItem value={value} className="border rounded last:border-b">
      <AccordionTrigger className="px-4">{t(title)}</AccordionTrigger>
      <AccordionContent className="px-4">
        <div className="space-y-2">
          <div className="pb-2">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("common.search")}
            />
          </div>
          <div
            onScroll={handleScroll}
            className="max-h-64 overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            {pinnedValues.map((v) => (
              <FilterOptionRow
                key={v}
                option={{ label: v, value: v }}
                checked={selectedValues.includes(v)}
                onToggle={() => onSelectionChange(toggleValue(selectedValues, v))}
                breakAnywhere
              />
            ))}
            {options.map((option) => (
              <FilterOptionRow
                key={option.value}
                option={option}
                checked={selectedValues.includes(option.value)}
                onToggle={() =>
                  onSelectionChange(toggleValue(selectedValues, option.value))
                }
                breakAnywhere
              />
            ))}
            {isLoading && (
              <div className="space-y-2 py-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            )}
            {isError && (
              <div className="flex items-center justify-between gap-2 py-2">
                <p className="text-sm text-destructive">
                  {t("commonSentences.optionsLoadFailed")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => retry()}
                >
                  {t("common.retry")}
                </Button>
              </div>
            )}
            {!isLoading &&
              !isError &&
              options.length === 0 &&
              pinnedValues.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">
                {t("commonSentences.noOptionsFound")}
              </p>
            )}
            {isFetchingMore && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function FiltersDrawer({
  open,
  onOpenChange,
  searchParams,
  filterKeys,
  filterSections,
  entityName,
  filtersLoading = false,
  totalCount,
  searchTag,
  preserveParamKeys = [],
  resetsPage = true,
  useFilteredCount,
  useAsyncFilterOptions,
  defaultExpanded = false,
  initialOverrides,
}: FiltersDrawerProps) {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();

  const getFiltersFromParams = useCallback((): Record<string, string[]> => {
    const record: Record<string, string[]> = {};
    filterKeys.forEach((key) => {
      record[key] = searchParams.getAll(key);
    });
    return record;
  }, [searchParams, filterKeys]);

  const [currentFilters, setCurrentFilters] =
    useState<Record<string, string[]>>(getFiltersFromParams);
  const [openAccordionValues, setOpenAccordionValues] = useState<string[]>([]);
  const drawerPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fromUrl = getFiltersFromParams();
    if (open && initialOverrides) {
      setCurrentFilters({ ...fromUrl, ...initialOverrides });
    } else {
      setCurrentFilters(fromUrl);
    }
  }, [searchParams, open, getFiltersFromParams, initialOverrides]);

  // Optionally start every section expanded when the drawer opens. Users can still collapse
  // sections while it stays open.
  useEffect(() => {
    if (defaultExpanded && open) {
      setOpenAccordionValues(filterSections.map((section) => section.key));
    }
  }, [defaultExpanded, open, filterSections]);
  const filteredCount = useFilteredCount?.(currentFilters);
  const displayCount = filteredCount ? filteredCount.count : totalCount;
  const displayCountLoading = filteredCount ? filteredCount.isLoading : false;

  const sourceSectionByKey = useMemo(() => {
    const map: Record<
      string,
      Extract<FilterSectionConfig, { type: "sources" }>
    > = {};
    filterSections.forEach((s) => {
      if ("type" in s && s.type === "sources") map[s.key] = s;
    });
    return map;
  }, [filterSections]);

  const handleApplyFilters = () => {
    const next = new URLSearchParams(searchParams);
    filterKeys.forEach((key) => next.delete(key));
    filterKeys.forEach((key) => {
      const values = currentFilters[key] ?? [];
      const sourceSection = sourceSectionByKey[key];
      if (
        sourceSection &&
        values.length === sourceSection.sourceOptions.length
      ) {
        return;
      }
      values.forEach((value) => next.append(key, value));
    });
    preserveParamKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value != null) next.set(key, value);
    });
    if (resetsPage) next.set(URL_PARAMS.PAGE, "1");
    setSearchParams(next);
    onOpenChange(false);
  };

  const handleClearFilters = () => {
    const defaultFilters: Record<string, string[]> = {};
    filterKeys.forEach((key) => {
      defaultFilters[key] = [];
    });
    setCurrentFilters(defaultFilters);
    const next = new URLSearchParams(searchParams);
    filterKeys.forEach((key) => next.delete(key));
    if (resetsPage) next.set(URL_PARAMS.PAGE, "1");
    setSearchParams(next);
  };

  const handleFilterChange = (key: string, values: string[]) => {
    setCurrentFilters((prev) => ({ ...prev, [key]: values }));
  };

  const optionSections = filterSections.filter(
    (s): s is FilterSectionConfig & { options: FilterOption<string>[] } =>
      "options" in s && Array.isArray(s.options),
  );
  const textSections = filterSections.filter(
    (s): s is Extract<FilterSectionConfig, { type: "text" }> =>
      "type" in s && s.type === "text",
  );
  const sourceSections = filterSections.filter(
    (s): s is Extract<FilterSectionConfig, { type: "sources" }> =>
      "type" in s && s.type === "sources",
  );
  // An async section counts even though its options are unknown until it is expanded — collapsed, it
  // has fetched nothing yet, and hiding it would mean hiding the whole drawer.
  const asyncSections = filterSections.filter(
    (s): s is Extract<FilterSectionConfig, { type: "async" }> =>
      "type" in s && s.type === "async",
  );
  const hasSections =
    optionSections.some((s) => s.options.length > 0) ||
    textSections.length > 0 ||
    sourceSections.length > 0 ||
    asyncSections.length > 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          requestAnimationFrame(() => {
            drawerPanelRef.current?.focus({ preventScroll: true });
          });
        }}
      >
        <DrawerHeader>
          <DrawerTitle>
            {t("admin.filters.filterTitle", { entity: entityName })}
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            {t("admin.filters.filterDescription", {
              entity: entityName,
              defaultValue: `Filter and refine the list of ${entityName}.`,
            })}
          </DrawerDescription>
        </DrawerHeader>

        <div
          ref={drawerPanelRef}
          tabIndex={-1}
          className="flex flex-col gap-4 overflow-y-auto px-4 outline-none"
        >
          {displayCount !== undefined && (
            <div className="rounded-lg border bg-muted/30 p-3">
              {displayCountLoading ? (
                <Skeleton className="h-5 w-40" />
              ) : (
                <span className="text-sm font-medium">
                  {t("admin.filters.totalRecords", {
                    count: displayCount,
                    entity: entityName,
                  })}
                </span>
              )}
            </div>
          )}

          {searchTag && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1.5">
                <Search className="h-3 w-3" />
                <span className="text-xs">"{searchTag}"</span>
              </Badge>
            </div>
          )}

          {(displayCount !== undefined || searchTag) && <Separator />}

          {filtersLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : hasSections ? (
            <Accordion
              type="multiple"
              value={openAccordionValues}
              onValueChange={setOpenAccordionValues}
              className="w-full flex flex-col gap-4 pb-4"
            >
              {filterSections.map((section) => {
                if ("type" in section && section.type === "async") {
                  if (!useAsyncFilterOptions) return null;
                  return (
                    <AsyncFilterSection
                      key={section.key}
                      value={section.key}
                      title={section.title}
                      expanded={openAccordionValues.includes(section.key)}
                      selectedValues={currentFilters[section.key] ?? []}
                      onSelectionChange={(values) =>
                        handleFilterChange(section.key, values)
                      }
                      useAsyncFilterOptions={useAsyncFilterOptions}
                    />
                  );
                }
                if ("type" in section && section.type === "sources") {
                  const selectedValues = currentFilters[section.key] ?? [];
                  const handleToggle = (source: string) => {
                    if (selectedValues.includes(source)) {
                      handleFilterChange(
                        section.key,
                        selectedValues.filter((v) => v !== source),
                      );
                    } else {
                      handleFilterChange(section.key, [
                        ...selectedValues,
                        source,
                      ]);
                    }
                  };
                  return (
                    <AccordionItem
                      key={section.key}
                      value={section.key}
                      className="border rounded-lg last:border-b"
                    >
                      <AccordionTrigger className="px-4">
                        {t(section.title)}
                      </AccordionTrigger>
                      <AccordionContent className="px-4">
                        <div className="flex flex-col gap-2">
                          {section.sourceOptions.map((source) => (
                            <Label
                              key={source}
                              className="flex items-center gap-2 cursor-pointer hover-tint-primary transition-colors rounded-md p-2 -mx-2"
                            >
                              <Checkbox
                                checked={selectedValues.includes(source)}
                                onCheckedChange={() => handleToggle(source)}
                              />
                              <span className="text-sm capitalize">
                                {source}
                              </span>
                            </Label>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                }
                if ("type" in section && section.type === "text") {
                  const values = currentFilters[section.key] ?? [];
                  const raw = values.join(", ");
                  return (
                    <AccordionItem
                      key={section.key}
                      value={section.key}
                      className="border rounded-lg last:border-b"
                    >
                      <AccordionTrigger className="px-4">
                        {t(section.title)}
                      </AccordionTrigger>
                      <AccordionContent className="px-4">
                        <Input
                          value={raw}
                          onChange={(e) => {
                            const next = e.target.value
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean);
                            handleFilterChange(section.key, next);
                          }}
                          placeholder={section.placeholder ?? ""}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  );
                }
                if ("options" in section && Array.isArray(section.options)) {
                  if (section.options.length === 0) return null;
                  return (
                    <FilterSection
                      key={section.key}
                      value={section.key}
                      title={section.title}
                      options={section.options}
                      selectedValues={currentFilters[section.key] ?? []}
                      onSelectionChange={(values) =>
                        handleFilterChange(section.key, values)
                      }
                    />
                  );
                }
                return null;
              })}
            </Accordion>
          ) : (
            <p className="text-sm text-muted-foreground py-2">
              {t("commonSentences.noOptionsFound")}
            </p>
          )}
        </div>

        <DrawerFooter className="w-full md:flex-row gap-2">
          <Button onClick={handleApplyFilters} className="md:w-1/2">
            {t("admin.filters.showFilterdData", { entity: entityName })}
          </Button>
          <Button
            variant="ghost"
            onClick={handleClearFilters}
            className="md:w-1/2"
          >
            <Trash2 className="h-4 w-4" />
            {t("admin.filters.resetAllFilters")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
